import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { DocumentStatus } from '@project/contracts'
import { Button, Empty, Icon, Money, Notice, Panel, StatusMark, Table, type ColumnDef } from '../design/index.js'
import { useApi, type ApprovalItem, type NotificationItem, type TaskItem } from '../app/api.js'
import { Link } from '../app/Link.js'
import { navigate, paths } from '../app/router.js'
import { useDocumentMenu } from '../framework/queries.js'
import { ErrorBlock, Loading } from '../framework/feedback.js'
import { formatDate } from '../framework/format.js'
import { StarToggle, favouritePath } from '../framework/favourites.js'

const useInvalidateInbox = () => {
  const client = useQueryClient()
  return () => {
    void client.invalidateQueries({ queryKey: ['inboxCounts'] })
    void client.invalidateQueries({ queryKey: ['approvals'] })
    void client.invalidateQueries({ queryKey: ['tasks'] })
    void client.invalidateQueries({ queryKey: ['notifications'] })
  }
}

/** Documents waiting for THIS person to sign. Derived, never stored. */
export const ApprovalsPage = ({ companyId }: { companyId: string }) => {
  const api = useApi()
  const q = useQuery({ queryKey: ['approvals', api.companyId], queryFn: () => api.approvals() })

  if (q.isLoading) return <div className="page"><Loading /></div>
  if (q.error) return <div className="page"><ErrorBlock error={q.error} /></div>
  const rows = [...(q.data ?? [])]

  const columns: ColumnDef<ApprovalItem>[] = [
    { key: 'documentNo', header: 'Document no', width: 158,
      render: (r) => <span className="mono">{r.documentNo ?? '(draft)'}</span> },
    { key: 'docTypeLabel', header: 'Type', width: 190 },
    { key: 'documentDate', header: 'Date', width: 130, render: (r) => <span className="num">{formatDate(r.documentDate)}</span> },
    { key: 'partyName', header: 'Party' },
    { key: 'projectName', header: 'Project' },
    { key: 'stageName', header: 'Waiting at', width: 200 },
    { key: 'waitingDays', header: 'For', width: 110, align: 'right',
      render: (r) => <span className="num">{r.waitingDays === 0 ? 'today' : `${r.waitingDays} d`}</span> },
    { key: 'total', header: 'Value', width: 150, align: 'right', render: (r) => <Money value={r.total} /> },
  ]

  return (
    <div className="page">
      <h1 className="page-title">Approvals</h1>
      <p className="page-subtitle">Everything waiting for your signature. Only what you can actually sign is listed.</p>
      {rows.some((r) => r.approvedWithExceptions) ? (
        <div style={{ margin: '18px 0' }}>
          <Notice kind="warn">
            Some of these were submitted with exceptions. Open one to see the rules it broke before you approve it.
          </Notice>
        </div>
      ) : null}
      {rows.length === 0 ? (
        <Empty title="Nothing waiting for you"
          body="When someone submits a document you can sign, it appears here and on the Approvals badge." />
      ) : (
        <Table columns={columns} rows={rows} getKey={(r) => r.id}
          onRowClick={(r) => navigate(paths.document(companyId, r.route, r.id))} />
      )}
    </div>
  )
}

/** The work list. Open items first, overdue called out. */
export const TasksPage = ({ companyId }: { companyId: string }) => {
  const api = useApi()
  const invalidate = useInvalidateInbox()
  const q = useQuery({ queryKey: ['tasks', api.companyId], queryFn: () => api.tasks() })
  const done = useMutation({
    mutationFn: (id: string) => api.setTaskState(id, 'DONE'),
    onSuccess: invalidate,
  })

  if (q.isLoading) return <div className="page"><Loading /></div>
  if (q.error) return <div className="page"><ErrorBlock error={q.error} /></div>
  const rows = [...(q.data ?? [])]

  const columns: ColumnDef<TaskItem>[] = [
    { key: 'title', header: 'Task' },
    { key: 'detail', header: 'Detail' },
    { key: 'dueDate', header: 'Due', width: 150,
      render: (r) => r.dueDate
        ? <span className="num" style={r.overdue ? { color: 'var(--bad)', fontWeight: 600 } : undefined}>
            {formatDate(r.dueDate)}{r.overdue ? ' · overdue' : ''}
          </span>
        : null },
    { key: 'actions', header: '', width: 150,
      render: (r) => (
        <Button kind="ghost" disabled={done.isPending}
          onClick={(e) => { e?.stopPropagation?.(); done.mutate(r.id) }}>Mark done</Button>
      ) },
  ]

  return (
    <div className="page">
      <h1 className="page-title">Tasks</h1>
      <p className="page-subtitle">What the system is asking you to do, rather than what you have to remember.</p>
      {rows.length === 0 ? (
        <Empty title="Nothing to do"
          body="Tasks are opened automatically — an approval to give, a receipt to record. They close themselves when the work is done." />
      ) : (
        <Table columns={columns} rows={rows} getKey={(r) => r.id}
          onRowClick={(r) => { if (r.route && r.documentId) navigate(paths.document(companyId, r.route, r.documentId)) }} />
      )}
    </div>
  )
}

const MARK_FOR: Record<string, DocumentStatus> = {
  APPROVED: 'APPROVED',
  CANCELLED: 'CANCELLED',
  // Sent back is a return to draft, and the mark says so rather than reading
  // like a cancellation.
  REJECTED: 'DRAFT',
  APPROVAL_REQUEST: 'IN_REVIEW',
}

/** The notification centre, unread first. */
export const AlertsPage = ({ companyId }: { companyId: string }) => {
  const api = useApi()
  const invalidate = useInvalidateInbox()
  const q = useQuery({ queryKey: ['notifications', api.companyId], queryFn: () => api.notifications() })
  const readAll = useMutation({ mutationFn: () => api.markNotificationsRead(), onSuccess: invalidate })
  const readOne = useMutation({ mutationFn: (id: string) => api.markNotificationsRead(id), onSuccess: invalidate })

  if (q.isLoading) return <div className="page"><Loading /></div>
  if (q.error) return <div className="page"><ErrorBlock error={q.error} /></div>
  const rows = [...(q.data ?? [])]
  const unread = rows.filter((r) => r.readAt === null)

  const open = (n: NotificationItem) => {
    if (n.readAt === null) readOne.mutate(n.id)
    if (n.route && n.documentId) navigate(paths.document(companyId, n.route, n.documentId))
  }

  return (
    <div className="page">
      {/*
        The reference's Notification Center: one card, the new ones under
        their own heading with Mark all read beside it, then what has been
        read already.
      */}
      <h1 className="page-title">Notification Center</h1>
      <p className="page-subtitle">You can see all your notifications here.</p>

      {rows.length === 0 ? (
        <Empty title="Nothing yet"
          body="When a document is sent for your approval, or one of yours is approved, you will hear about it here." />
      ) : (
        <>
          <Panel title="New notifications"
            aside={unread.length > 0 ? (
              <button type="button" className="linkish" disabled={readAll.isPending} onClick={() => readAll.mutate()}>
                Mark all read
              </button>
            ) : undefined}>
            {unread.length === 0 ? <p className="muted">You have read everything.</p> : (
              <div className="alerts">
                {unread.map((n) => (
                  <button type="button" key={n.id} className="alert alert--unread" onClick={() => open(n)}>
                    <span className="alert__mark">
                      <StatusMark status={MARK_FOR[n.kind] ?? 'IN_REVIEW'} size={14} />
                    </span>
                    <NotificationText n={n} />
                  </button>
                ))}
              </div>
            )}
          </Panel>
          {rows.length > unread.length ? (
            <Panel title="Earlier">
              <div className="alerts">
                {rows.filter((r) => r.readAt !== null).map((n) => (
                  <button type="button" key={n.id} className="alert" onClick={() => open(n)}>
                    <span className="alert__mark">
                      <StatusMark status={MARK_FOR[n.kind] ?? 'IN_REVIEW'} size={14} />
                    </span>
                    <NotificationText n={n} />
                  </button>
                ))}
              </div>
            </Panel>
          ) : null}
        </>
      )}
    </div>
  )
}

/** "6 Oct" — the day an alert arrived, as the reference prints it. */
const DAY_MONTH = new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short' })

/**
 * One notification, in the reference's three lines (HOME-009):
 *
 *   **Sent for your approval:** CHAKARA/PO#4709 (Purchase order)
 *   Project: PRO/261 - DURR - TATA MOTORS (Refurbishment)
 *   Kalaivani • 6 Oct • Chakara Engineering
 *
 * The body is kept where it says something the three lines do not — why a
 * document was sent back — or where there is no project to name.
 */
export const NotificationText = ({ n }: { n: NotificationItem }) => {
  const cut = n.title.indexOf(': ')
  const event = cut > 0 ? n.title.slice(0, cut + 1) : null
  const subject = cut > 0 ? n.title.slice(cut + 2) : n.title
  const showBody = n.body && (!n.projectLine || n.kind === 'REJECTED')
  const meta = [n.actorName, DAY_MONTH.format(new Date(n.createdAt)), n.companyName].filter(Boolean).join(' • ')
  return (
    <span className="alert__body">
      <span className="alert__title">
        {event ? <strong>{event} </strong> : null}{subject}
      </span>
      {n.projectLine ? <span className="alert__detail">{n.projectLine}</span> : null}
      {showBody ? <span className="alert__detail">{n.body}</span> : null}
      <span className="alert__meta">{meta}</span>
    </span>
  )
}

/** Starred pages, from the Transactions and Masters menus. */
export const FavouritesPage = ({ companyId }: { companyId: string }) => {
  const api = useApi()
  // Resolved by the server from every hub, in the order placed (HOME-005).
  const home = useQuery({ queryKey: ['home', companyId], queryFn: () => api.home() })

  if (home.isLoading) return <div className="page"><Loading /></div>
  const starred = home.data?.favourites ?? []

  return (
    <div className="page">
      <h1 className="page-title">Favourites</h1>
      <p className="page-subtitle">The pages you starred, so the ones you use daily are one click away.</p>
      {starred.length === 0 ? (
        <Empty title="Nothing starred yet"
          body="Star a page on the Transactions, Masters, Reports or Dashboards menu and it will be listed here and on your home page." />
      ) : (
        <Panel title="Starred" aside={`${starred.length}`}>
          <div className="tile-grid">
            {starred.map((d) => (
              <div className="tile" key={d.key}>
                <StarToggle menuKey={d.key} label={d.label} />
                <div className="tile__body">
                  <Link to={favouritePath(companyId, d)} className="tile__title">{d.label}</Link>
                  <p className="tile__desc">{d.hub}</p>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      )}
    </div>
  )
}

/** Help. Short, and about this system rather than about software in general. */
export const HelpPage = ({ companyId }: { companyId: string }) => (
  <div className="page">
    <h1 className="page-title">Help</h1>
    <p className="page-subtitle">How this system is put together, and where to look when something is not where you expect.</p>

    <Panel title="How a document moves">
      <ol className="help-list">
        <li><strong>Draft.</strong> Nothing is reserved and no number is issued, so an abandoned draft costs nothing.</li>
        <li><strong>Submitted.</strong> The number is issued here, the approval route is fixed to the document, and the rules run.</li>
        <li><strong>Approved.</strong> Each stage is signed in turn. Only then does the document count against an order.</li>
        <li><strong>Cancelled.</strong> Anything it had reserved is released immediately.</li>
      </ol>
    </Panel>

    <Panel title="If a rule stops you">
      <p className="muted">
        A rule that can be overridden will say so, and offer both choices. If you override it, the document is
        marked <strong>approved with exceptions</strong> and the table of what it broke stays on it permanently —
        your approver sees exactly what you saw.
      </p>
    </Panel>

    <Panel title="Where things live">
      <ul className="help-list">
        <li><Link to={paths.hub(companyId, 'transactions')}>Transactions</Link> — everything the business raises, grouped by what it is for.</li>
        <li><Link to={paths.hub(companyId, 'masters')}>Masters</Link> — the reference data every document is built from.</li>
        <li><Link to={paths.hub(companyId, 'reports')}>Reports</Link> — read across everything. Nothing runs until you apply the filters.</li>
        <li><Link to={paths.hub(companyId, 'dashboards')}>Dashboards</Link> — the shape of the business rather than a list.</li>
      </ul>
    </Panel>

    <Panel title="Still stuck">
      <p className="muted">
        Every screen shows why a list is empty and what to do about it. If a page will not load, the message names
        the reason — take a note of it before you reload, and give it to whoever supports this system.
      </p>
    </Panel>
  </div>
)
